import { useState } from 'react';
import { Copy, Check, Link } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { useGenerateInquiryLink } from './useGenerateInquiryLink';

interface InquiryLinkCardProps {
  inquiryToken: string | null;
  schoolYearId: string;
  onGenerated: () => void;
}

export function InquiryLinkCard({ inquiryToken, schoolYearId, onGenerated }: InquiryLinkCardProps) {
  const [copied, setCopied] = useState(false);
  const [generateLink, { loading }] = useGenerateInquiryLink();

  const inquiryUrl = inquiryToken
    ? `${window.location.origin}/app/inquire/${inquiryToken}`
    : null;

  const handleGenerate = async () => {
    try {
      const result = await generateLink({
        variables: { input: { schoolYearId } },
      });
      const data = result.data?.generateInquiryLink;
      if (data?.userErrors && data.userErrors.length > 0) {
        return;
      }
      onGenerated();
    } catch {
      // error handled by Apollo
    }
  };

  const handleCopy = async () => {
    if (!inquiryUrl) return;
    await navigator.clipboard.writeText(inquiryUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Link className="h-4 w-4" />
          Inquiry Link
        </CardTitle>
        <CardDescription>
          Share this link to allow prospective families to submit admission inquiries.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {inquiryUrl ? (
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-md bg-muted px-3 py-2 text-sm break-all">
              {inquiryUrl}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        ) : (
          <Button onClick={handleGenerate} disabled={loading} variant="outline">
            {loading ? 'Generating...' : 'Generate Inquiry Link'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
